import {Table, TableBody, TableHeader, TableRow} from '~/design-system/foundations'
import {HgTableTitleCell} from '~/design-system/hg/components/HgTable'
import {dollarsPrettifier} from '~/utils/dollarsPrettifier'
import {type BankFeeProps} from '../../types'
import {comparisonTableVariants} from './variants'
import {
  ComparisonTableTitleCell,
  ComparisonTableBodyCell,
  UsageBasedCell,
  PerTransactionCell,
  TotalCell,
} from './Cell'
import {getPercentageFeeCost} from '../../utils'

type ComparisonTableProps = {
  legacyBankFees: BankFeeProps
  mercuryBankFees: BankFeeProps
  legacyBankTotalMonthlyFees: number
  mercuryBankTotalMonthlyFees: number
}

const ComparisonTable = ({
  legacyBankFees,
  mercuryBankFees,
  legacyBankTotalMonthlyFees,
  mercuryBankTotalMonthlyFees,
}: ComparisonTableProps) => {
  const {row} = comparisonTableVariants()

  return (
    <Table classNames={{table: 'border-b-0'}}>
      <TableHeader>
        <TableRow className={row()}>
          <HgTableTitleCell type="empty" className="hidden md:block" />
          <ComparisonTableTitleCell title="Legacy banks" color="beige" />
          <ComparisonTableTitleCell title="Mercury" color="primary" />
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow className={row()}>
          <ComparisonTableBodyCell
            variant="rowTitle"
            title="Maintenance fees"
            hintProps={{
              variant: 'rich',
              title: 'Maintenance fees',
              content:
                'A monthly fee many banks charge just for keeping your account open.',
            }}
          />
          <ComparisonTableBodyCell
            body={dollarsPrettifier(
              legacyBankFees.maintenanceFee,
              true,
              'twoWhenNecessary'
            )}
          />
          <ComparisonTableBodyCell
            body={dollarsPrettifier(
              mercuryBankFees.maintenanceFee,
              true,
              'twoWhenNecessary'
            )}
          />
        </TableRow>
        <TableRow className={row()}>
          <ComparisonTableBodyCell
            variant="rowTitle"
            title="Transaction overage fees"
            hintProps={{
              variant: 'rich',
              title: 'Transaction overage fees',
              content: `Many banks charge fees when you exceed a monthly limit across all incoming and outgoing transactions (including card payments, wires, checks, etc.) At Mercury, we only charge an ACH usage fee for when you exceed ${dollarsPrettifier(
                mercuryBankFees.transactionFee.limit,
                false,
                0
              )} ACH transactions per month.`,
            }}
          />
          <UsageBasedCell {...legacyBankFees.transactionFee} />
          <UsageBasedCell {...mercuryBankFees.transactionFee} />
        </TableRow>
        <TableRow className={row()}>
          <ComparisonTableBodyCell
            variant="rowTitle"
            title="Domestic wire (USD) fees"
            hintProps={{
              variant: 'rich',
              title: 'Domestic wire (USD) fees',
              content:
                'Many banks charge a fee for each domestic wire you send and receive.',
            }}
          />
          <ComparisonTableBodyCell type="empty" />
          <ComparisonTableBodyCell type="empty" />
        </TableRow>
        <TableRow className={row()}>
          <ComparisonTableBodyCell
            variant="rowTitle"
            body="Outgoing"
            leftIconType="arrow-left"
          />
          <PerTransactionCell {...legacyBankFees.domesticUSDWireFee.outgoing} />
          <PerTransactionCell {...mercuryBankFees.domesticUSDWireFee.outgoing} />
        </TableRow>
        <TableRow className={row()}>
          <ComparisonTableBodyCell
            variant="rowTitle"
            body="Incoming"
            leftIconType="arrow-right"
          />
          <PerTransactionCell {...legacyBankFees.domesticUSDWireFee.incoming} />
          <PerTransactionCell {...mercuryBankFees.domesticUSDWireFee.incoming} />
        </TableRow>
        <TableRow className={row()}>
          <ComparisonTableBodyCell
            variant="rowTitle"
            title="International wire (USD) fees"
            hintProps={{
              variant: 'rich',
              title: 'International wire (USD) fees',
              content:
                'Many banks charge a fee for each international wire you send and receive.',
            }}
          />
          <ComparisonTableBodyCell type="empty" />
          <ComparisonTableBodyCell type="empty" />
        </TableRow>
        <TableRow className={row()}>
          <ComparisonTableBodyCell
            variant="rowTitle"
            body="Outgoing"
            leftIconType="arrow-left"
          />
          <PerTransactionCell {...legacyBankFees.internationalUSDWireFee.outgoing} />
          <PerTransactionCell
            {...mercuryBankFees.internationalUSDWireFee.outgoing}
          />
        </TableRow>
        <TableRow className={row()}>
          <ComparisonTableBodyCell
            variant="rowTitle"
            body="Incoming"
            leftIconType="arrow-right"
          />
          <PerTransactionCell {...legacyBankFees.internationalUSDWireFee.incoming} />
          <PerTransactionCell
            {...mercuryBankFees.internationalUSDWireFee.incoming}
          />
        </TableRow>
        <TableRow className={row()}>
          <ComparisonTableBodyCell
            variant="rowTitle"
            title="International wire (non-USD) fees"
            hintProps={{
              variant: 'rich',
              title: 'International wire (non-USD) fees',
              content: `Many providers bake hidden markups into their exchange rates — even on top of any currency conversion and wire fees. Mercury keeps it simple: a transparent ${mercuryBankFees.foreignCurrencyInternationalWireFee.percentageFee}% currency exchange fee, and the same market-leading rate you’d find on Google.`,
            }}
          />
          <ComparisonTableBodyCell type="empty" />
          <ComparisonTableBodyCell type="empty" />
        </TableRow>
        <TableRow className={row()}>
          <ComparisonTableBodyCell
            variant="rowTitle"
            body="Outgoing currency exchange fees"
            leftIconType="arrow-left"
          />
          <ComparisonTableBodyCell
            body={dollarsPrettifier(
              getPercentageFeeCost(
                legacyBankFees.foreignCurrencyInternationalWireFee
              ),
              true,
              'twoWhenNecessary'
            )}
            caption={`Estimated ${legacyBankFees.foreignCurrencyInternationalWireFee.percentageFee}% FX markup`}
          />
          <ComparisonTableBodyCell
            body={dollarsPrettifier(
              getPercentageFeeCost(
                mercuryBankFees.foreignCurrencyInternationalWireFee
              ),
              true,
              'twoWhenNecessary'
            )}
            caption={`Clear ${mercuryBankFees.foreignCurrencyInternationalWireFee.percentageFee}% FX fee`}
          />
        </TableRow>
        <TableRow className={row()}>
          <ComparisonTableBodyCell variant="rowTitle" title="Total monthly fees" />
          <TotalCell totalFees={legacyBankTotalMonthlyFees} />
          <TotalCell totalFees={mercuryBankTotalMonthlyFees} />
        </TableRow>
      </TableBody>
    </Table>
  )
}

export default ComparisonTable
